import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUfDiariaById, createUfDiaria, updateUfDiaria, deleteUfDiaria } from "../../services/ufDiariaService";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Separator } from "../../components/ui/separator";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { useContextualNavigation } from '../../hooks/useContextualNavigation';

export default function UfDiariaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const { getListRoute } = useContextualNavigation();
  const ufDiariaListRoute = getListRoute('uf-diaria');
  
  const [formData, setFormData] = useState({
    fecha: "",
    valor_clp: "",
    valor_clp_display: "", // Para mostrar con formato chileno
    status: "active"
  });
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Load data if editing
  useEffect(() => {
    if (isEditing) {
      const loadUfDiaria = async () => {
        try {
          setLoading(true);
          const data = await getUfDiariaById(id);
          setFormData({
            fecha: data.fecha || "",
            valor_clp: data.valor_clp || "",
            valor_clp_display: formatNumberForInput(data.valor_clp) || "",
            status: data.status || "active"
          });
        } catch (err) {
          console.error("Error loading UF diaria:", err);
          setError("No se pudo cargar la información de la UF diaria");
        } finally {
          setLoading(false);
        }
      };
      loadUfDiaria();
    }
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'valor_clp_display') {
      // Manejar el input con formato chileno
      const cleanValue = parseChileanNumber(value);
      setFormData(prev => ({
        ...prev,
        valor_clp_display: value, // Mantener el formato de entrada
        valor_clp: cleanValue // Convertir para la base de datos
      }));
    } else {
      const newValue = type === 'checkbox' ? (checked ? 'active' : 'inactive') : value;
      setFormData(prev => ({
        ...prev,
        [name]: newValue
      }));
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      setLoading(true);
      await deleteUfDiaria(id);
      navigate(ufDiariaListRoute || '/admin/uf-diaria');
    } catch (err) {
      console.error("Error deleting UF diaria:", err);
      setError("No se pudo eliminar la UF diaria");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    // Validaciones básicas
    if (!formData.fecha) {
      setError("La fecha es requerida");
      setSaving(false);
      return;
    }
    
    if (!formData.valor_clp_display || isNaN(parseFloat(parseChileanNumber(formData.valor_clp_display))) || parseFloat(parseChileanNumber(formData.valor_clp_display)) <= 0) {
      setError("El valor debe ser un número mayor a 0");
      setSaving(false);
      return;
    }
    
    try {
      const dataToSubmit = {
        ...formData,
        valor_clp: parseFloat(formData.valor_clp)
      };
      
      if (isEditing) {
        // For editing, only send changed fields
        const originalData = await getUfDiariaById(id);
        const changedData = {};
        
        if (formData.fecha !== originalData.fecha) changedData.fecha = formData.fecha;
        if (parseFloat(formData.valor_clp) !== parseFloat(originalData.valor_clp)) changedData.valor_clp = parseFloat(formData.valor_clp);
        if (formData.status !== originalData.status) changedData.status = formData.status;
        
        await updateUfDiaria(id, changedData);
      } else {
        // For creating, send all data
        await createUfDiaria(dataToSubmit);
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate("/admin/uf-diaria");
      }, 1500);
    } catch (err) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} UF diaria:`, err);
      setError(`No se pudo ${isEditing ? 'actualizar' : 'crear'} la UF diaria`);
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Asegurar que la fecha se mantenga en el formato correcto sin conversión de zona horaria
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    return dateString;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00.000Z');
    return date.toLocaleDateString('es-CL', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatNumberForInput = (value) => {
    if (!value) return '';
    // Convertir número a formato chileno con coma decimal
    return value.toString().replace('.', ',');
  };

  const parseChileanNumber = (value) => {
    if (!value) return '';
    // Convertir formato chileno (con coma) a formato estándar (con punto)
    return value.toString().replace(',', '.');
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    // Convertir string a número si es necesario
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 2
    }).format(numValue);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">{id ? 'Editar' : 'Nueva'} UF Diaria</h1>
        {isEditing && (
          <Button 
            variant="destructive" 
            onClick={() => setDeleteDialogOpen(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            Eliminar
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert>
          <AlertDescription>
            UF diaria {isEditing ? 'actualizada' : 'creada'} correctamente
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="fecha">Fecha</Label>
                <Input
                  id="fecha"
                  name="fecha"
                  type="date"
                  value={formatDate(formData.fecha)}
                  onChange={handleChange}
                  required
                />
                {formData.fecha && (
                  <p className="text-sm text-gray-600">
                    Fecha: {formatDateForDisplay(formData.fecha)}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="valor_clp_display">Valor UF (CLP)</Label>
                <Input
                  id="valor_clp_display"
                  name="valor_clp_display"
                  type="text"
                  value={formData.valor_clp_display}
                  onChange={handleChange}
                  placeholder="Ej: 35000,50"
                  required
                />
                {formData.valor_clp && (
                  <p className="text-sm text-gray-600">
                    Formato: {formatCurrency(formData.valor_clp)}
                  </p>
                )}
              </div>
            </div>

            <Separator />

            <div className="flex items-center space-x-2">
              <Switch
                id="status"
                name="status"
                checked={formData.status === 'active'}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({
                    ...prev,
                    status: checked ? 'active' : 'inactive'
                  }))
                }
              />
              <Label htmlFor="status" className="cursor-pointer">
                Estado activo
              </Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate("/admin/uf-diaria")}
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            disabled={saving}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear')}
          </Button>
        </div>
      </form>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el valor de UF del {formatDate(formData.fecha)}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}